import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import FocusableButton from 'components/common/FocusableButton';
import HBox from 'components/common/HBox';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';
import useFloating from 'hooks/layers/useFloating';
import React from 'react';
import useRect from 'hooks/common/useRect';
import jsPDF from 'jspdf';
import PlusJakartaSansBold from 'fonts/PlusJakartaSans-Bold';
import PlusJakartaSansBoldItalic from 'fonts/PlusJakartaSans-BoldItalic';
import PlusJakartaSansExtraBold from 'fonts/PlusJakartaSans-ExtraBold';
import PlusJakartaSansExtraBoldItalic from 'fonts/PlusJakartaSans-ExtraBoldItalic';
import PlusJakartaSansExtraLight from 'fonts/PlusJakartaSans-ExtraLight';
import PlusJakartaSansExtraLightItalic from 'fonts/PlusJakartaSans-ExtraLightItalic';
import PlusJakartaSansItalic from 'fonts/PlusJakartaSans-Italic';
import PlusJakartaSansLight from 'fonts/PlusJakartaSans-Light';
import PlusJakartaSansLightItalic from 'fonts/PlusJakartaSans-LightItalic';
import PlusJakartaSansMedium from 'fonts/PlusJakartaSans-Medium';
import PlusJakartaSansMediumItalic from 'fonts/PlusJakartaSans-MediumItalic';
import PlusJakartaSansRegular from 'fonts/PlusJakartaSans-Regular';
import PlusJakartaSansSemiBold from 'fonts/PlusJakartaSans-SemiBold';
import PlusJakartaSansSemiBoldItalic from 'fonts/PlusJakartaSans-SemiBoldItalic';
import PretendardBlack from 'fonts/Pretendard-Black';
import PretendardBold from 'fonts/Pretendard-Bold';
import PretendardExtraBold from 'fonts/Pretendard-ExtraBold';
import PretendardExtraLight from 'fonts/Pretendard-ExtraLight';
import PretendardLight from 'fonts/Pretendard-Light';
import PretendardMedium from 'fonts/Pretendard-Medium';
import PretendardRegular from 'fonts/Pretendard-Regular';
import PretendardSemiBold from 'fonts/Pretendard-SemiBold';
import PretendardThin from 'fonts/Pretendard-Thin';
import * as Luxon from 'luxon'

import {
  drawPage1,
  drawPage2,
  drawPage3,
  drawPage4,
  drawPage5,
  drawPage6,
  drawPage7,
  drawPage8,
  drawPage9,
  drawPage10,
} from 'utils/pdf';
import usePdfDisabled from 'hooks/app/usePdfDisabled';
import usePdfReset from 'hooks/app/usePdfReset';
import useNavigation from 'hooks/app/useNavigation';
import useCategorizedPages from 'hooks/app/useCategorizedPages';
import useNavAction from 'hooks/actions/useNavAction';
import { luxonDiffDay } from 'utils/time';

const defaultRenderer = (item) => (
  <div>
    {item.label}
  </div>
);

const items = [
  { key: 'A', label: T('전체') },
  { key: 'B', label: T('혈당') },
  { key: 'D', label: T('식사') },
  { key: 'E', label: T('활동') },
  { key: 'I', label: T('인슐린') }
];

const DOWNLOAD_ID = 'Download';
let download_processing = false;
export default React.forwardRef(function DownloadButton(props, outerRef) {
  const {
    tags,
    itemRenderer = defaultRenderer,
  } = props;

  const containerRef = React.useRef(null);
  const rect = useRect(containerRef);

  const [list, setList] = Recoil.useRecoilState(AppStore.floatings.list('App'));
  const [hash, setHash] = Recoil.useRecoilState(AppStore.floatings.hash('App'));
  const {
    open,
    update,
    close,
  } = useFloating({ setList, setHash });

  const ref = React.useRef(null);
  React.useImperativeHandle(outerRef, () => ref.current, []);

  const [active, setActive] = React.useState(false);
  const disactive = React.useCallback(() => {
    setActive(() => false);
  }, []);

  const onOuterClick = React.useCallback((ref) => (e) => {
    if (!ref?.current?.contains(e.target)) {
      close(DOWNLOAD_ID, disactive);
    }
  }, [close, disactive]);
  const onClose = React.useCallback((e) => {
    close(DOWNLOAD_ID, disactive);
  }, [close, disactive]);

  const [pdf_targets, setPdfTargets] = Recoil.useRecoilState(AppStore.assets.pdf_targets);
  const onCheck = React.useCallback((item) => (e) => {
    setPdfTargets(pdf_targets => {
      const nextValues = {
        ...pdf_targets,
        [item.key]: !pdf_targets[item.key],
      };
      if (item.key === 'A') {
        items.forEach(({ key }) => {
          nextValues[key] = nextValues.A;
        });
      } else {
        const [_, ...rest] = items;
        nextValues.A = rest.every(({ key }) => nextValues[key]);
      }
      return nextValues;
    })
  }, []);

  const glocose_data = Recoil.useRecoilValue(AppStore.assets.data('ReportBloodGlocose'));
  const diet_data = Recoil.useRecoilValue(AppStore.assets.data('ReportDiet'));
  const calories_data = Recoil.useRecoilValue(AppStore.assets.data('ReportExerciseCalories'));
  const steps_data = Recoil.useRecoilValue(AppStore.assets.data('ReportExerciseSteps'));
  const insulin_data = Recoil.useRecoilValue(AppStore.assets.data('ReportInsulin'));

  const [downloading, setDownloading] = Recoil.useRecoilState(AppStore.assets.data('PdfDownloading'));

  const glocose_high_list = Recoil.useRecoilValue(AppStore.assets.data('GlocoseHighList'));
  const glocose_low_list = Recoil.useRecoilValue(AppStore.assets.data('GlocoseLowList'));

  const glocose_profile_chart = Recoil.useRecoilValue(AppStore.assets.data('GlocoseProfileChart'));
  const glocose_tir_chart = Recoil.useRecoilValue(AppStore.assets.data('GlocoseTIRChart'));
  const glocose_distrubution_bar_chart = Recoil.useRecoilValue(AppStore.assets.data('GlocoseDistributionBarChart'));
  const glocose_distrubution_scatter_chart = Recoil.useRecoilValue(AppStore.assets.data('GlocoseDistributionScatterChart'));
  const glocose_pattern_weekday_chart = Recoil.useRecoilValue(AppStore.assets.data('GlocosePatternWeekdayChart'));
  const glocose_pattern_weekend_chart = Recoil.useRecoilValue(AppStore.assets.data('GlocosePatternWeekendChart'));
  const diet_distrubution_doughnut_chart = Recoil.useRecoilValue(AppStore.assets.data('DietDistributionDoughnutChart'));
  const diet_distrubution_scatter_chart = Recoil.useRecoilValue(AppStore.assets.data('DietDistributionScatterChart'));
  const diet_meal_analysis_list = Recoil.useRecoilValue(AppStore.assets.data('DietMealAnalysisList'));
  const diet_food_analysis_list = Recoil.useRecoilValue(AppStore.assets.data('DietFoodAnalysisList'));
  const diet_statistics_chart = Recoil.useRecoilValue(AppStore.assets.data('DietStatisticsChart'));
  const diet_daily_statistics = Recoil.useRecoilValue(AppStore.assets.data('DietDailyStatistics'));
  const step_analysis_day_chart = Recoil.useRecoilValue(AppStore.assets.data('StepAnalysisDayChart'));
  const step_analysis_week_chart = Recoil.useRecoilValue(AppStore.assets.data('StepAnalysisWeekChart'));
  const step_statistics_chart = Recoil.useRecoilValue(AppStore.assets.data('StepStatisticsChart'));
  const calory_analysis_day_chart = Recoil.useRecoilValue(AppStore.assets.data('CaloryAnalysisDayChart'));
  const calory_analysis_week_chart = Recoil.useRecoilValue(AppStore.assets.data('CaloryAnalysisWeekChart'));
  const calory_statistics_chart = Recoil.useRecoilValue(AppStore.assets.data('CaloryStatisticsChart'));
  const exercise_daily_statistics_list = Recoil.useRecoilValue(AppStore.assets.data('ExerciseDailyStatisticsList'));
  const insulin_distrubution_doughnut_chart = Recoil.useRecoilValue(AppStore.assets.data('InsulinDistributionDoughnutChart'));
  const insulin_distrubution_scatter_chart = Recoil.useRecoilValue(AppStore.assets.data('InsulinDistributionScatterChart'));
  const insulin_bolus_analysis_list = Recoil.useRecoilValue(AppStore.assets.data('InsulinBolusAnalysisList'));
  const insulin_statistics_chart = Recoil.useRecoilValue(AppStore.assets.data('InsulinStatisticsChart'));
  const insulin_pattern_time_list = Recoil.useRecoilValue(AppStore.assets.data('InsulinPatternTimeList'));
  const insulin_statistics_day_chart = Recoil.useRecoilValue(AppStore.assets.data('InsulinStatisticsDayChart'));
  const insulin_stat_list = Recoil.useRecoilValue(AppStore.assets.data('InsulinStatList'));

  const floatingInfo = React.useMemo(() => ({
    id: DOWNLOAD_ID,
    reference: ref,
    placement: 'bottom-right',
    connection: 'none',
    width: rect.width,
    height: 'auto',
    veilable: true,
    stickable: true,
    movable: false,
    closable: false,
    escapable: false,
    modal: true,
    type: DOWNLOAD_ID,
    tags,
    events: {
      onOuterClick,
      onClose,
      onCheck,
    },
    props: {
      renderer: itemRenderer,
      items,
      values: pdf_targets,
      disabled: false,
    },
  }), [itemRenderer, onCheck, onClose, onOuterClick, rect.width, tags, pdf_targets]);

  const doctor_info = Recoil.useRecoilValue(AppStore.assets.doctor_info);
  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  const period_start = Recoil.useRecoilValue(AppStore.assets.period_start);
  const period_end = Recoil.useRecoilValue(AppStore.assets.period_end);
  const patient_hash = Recoil.useRecoilValue(AppStore.assets.patient_hash);

  const luxon_start = React.useMemo(() => Luxon.DateTime.fromFormat(period_start, 'yyyyMMdd'), [period_start]);
  const luxon_end = React.useMemo(() => Luxon.DateTime.fromFormat(period_end, 'yyyyMMdd'), [period_end]);
  const diff_days = React.useMemo(() => luxonDiffDay(luxon_start, luxon_end) + 1, [luxon_end, luxon_start]);

  const disabled = usePdfDisabled();
  const reset = usePdfReset();

  const {
    onTrigger: onTriggerOverview,
  } = useNavAction('Overview', { category: 'App' });
  const {
    onTrigger: onTriggerReport,
  } = useNavAction('Report', { category: 'App' });

  const onDownload = React.useCallback(async () => {
    if (download_processing) return;
    download_processing = true;

    let range = []
    if (pdf_targets.B && pdf_targets.D && pdf_targets.E && pdf_targets.I) {
      range = [T('전체')];
    } else {
      if (pdf_targets.B) range.push(T('혈당'));
      if (pdf_targets.D) range.push(T('식사'));
      if (pdf_targets.E) range.push(T('활동'));
      if (pdf_targets.I) range.push(T('인슐린'));
    }

    const title = T('{0}_{1}일({2}-{3})_WellSee보고서({4})', patient_no, diff_days, period_start, period_end, range.join(','));
    const filename = T('report{0}.pdf', Luxon.DateTime.now().toFormat('yyyyMMdd.HHmmss'));

    var doc = new jsPDF({
      orientation: 'portrait',
      unit: 'px',
      format: 'a4',
      putOnlyUsedFonts: true,
    });

    doc.addFileToVFS('PlusJakartaSans-Bold.ttf', PlusJakartaSansBold);
    doc.addFileToVFS('PlusJakartaSans-BoldItalic.ttf', PlusJakartaSansBoldItalic);
    doc.addFileToVFS('PlusJakartaSans-ExtraBold.ttf', PlusJakartaSansExtraBold);
    doc.addFileToVFS('PlusJakartaSans-ExtraBoldItalic.ttf', PlusJakartaSansExtraBoldItalic);
    doc.addFileToVFS('PlusJakartaSans-ExtraLight.ttf', PlusJakartaSansExtraLight);
    doc.addFileToVFS('PlusJakartaSans-ExtraLightItalic.ttf', PlusJakartaSansExtraLightItalic);
    doc.addFileToVFS('PlusJakartaSans-Italic.ttf', PlusJakartaSansItalic);
    doc.addFileToVFS('PlusJakartaSans-Light.ttf', PlusJakartaSansLight);
    doc.addFileToVFS('PlusJakartaSans-LightItalic.ttf', PlusJakartaSansLightItalic);
    doc.addFileToVFS('PlusJakartaSans-Medium.ttf', PlusJakartaSansMedium);
    doc.addFileToVFS('PlusJakartaSans-MediumItalic.ttf', PlusJakartaSansMediumItalic);
    doc.addFileToVFS('PlusJakartaSans-Regular.ttf', PlusJakartaSansRegular);
    doc.addFileToVFS('PlusJakartaSans-SemiBold.ttf', PlusJakartaSansSemiBold);
    doc.addFileToVFS('PlusJakartaSans-SemiBoldItalic.ttf', PlusJakartaSansSemiBoldItalic);

    doc.addFileToVFS('Pretendard-Black.ttf', PretendardBlack); // 900
    doc.addFileToVFS('Pretendard-ExtraBold.ttf', PretendardExtraBold); // 800
    doc.addFileToVFS('Pretendard-Bold.ttf', PretendardBold); // 700
    doc.addFileToVFS('Pretendard-SemiBold.ttf', PretendardSemiBold); // 600
    doc.addFileToVFS('Pretendard-Medium.ttf', PretendardMedium); // 500
    doc.addFileToVFS('Pretendard-Regular.ttf', PretendardRegular); // 400
    doc.addFileToVFS('Pretendard-Light.ttf', PretendardLight); // 300
    doc.addFileToVFS('Pretendard-ExtraLight.ttf', PretendardExtraLight); // 200
    doc.addFileToVFS('Pretendard-Thin.ttf', PretendardThin); // 100

    doc.addFont('PlusJakartaSans-ExtraBold.ttf', 'PlusJakartaSansExtraBold', 'normal');
    doc.addFont('PlusJakartaSans-ExtraBoldItalic.ttf', 'PlusJakartaSansExtraBoldItalic', 'normal');
    doc.addFont('PlusJakartaSans-Bold.ttf', 'PlusJakartaSansBold', 'normal');
    doc.addFont('PlusJakartaSans-BoldItalic.ttf', 'PlusJakartaSansBoldItalic', 'normal');
    doc.addFont('PlusJakartaSans-SemiBold.ttf', 'PlusJakartaSansSemiBold', 'normal');
    doc.addFont('PlusJakartaSans-SemiBoldItalic.ttf', 'PlusJakartaSansSemiBoldItalic', 'normal');
    doc.addFont('PlusJakartaSans-Medium.ttf', 'PlusJakartaSansMedium', 'normal');
    doc.addFont('PlusJakartaSans-MediumItalic.ttf', 'PlusJakartaSansMediumItalic', 'normal');
    doc.addFont('PlusJakartaSans-Regular.ttf', 'PlusJakartaSansRegular', 'normal');
    doc.addFont('PlusJakartaSans-Italic.ttf', 'PlusJakartaSansItalic', 'normal');
    doc.addFont('PlusJakartaSans-Light.ttf', 'PlusJakartaSansLight', 'normal');
    doc.addFont('PlusJakartaSans-LightItalic.ttf', 'PlusJakartaSansLightItalic', 'normal');
    doc.addFont('PlusJakartaSans-ExtraLight.ttf', 'PlusJakartaSansExtraLight', 'normal');
    doc.addFont('PlusJakartaSans-ExtraLightItalic.ttf', 'PlusJakartaSansExtraLightItalic', 'normal');

    doc.addFont('Pretendard-Black.ttf', 'PretendardBlack', 'normal');
    doc.addFont('Pretendard-ExtraBold.ttf', 'PretendardExtraBold', 'normal');
    doc.addFont('Pretendard-Bold.ttf', 'PretendardBold', 'normal');
    doc.addFont('Pretendard-SemiBold.ttf', 'PretendardSemiBold', 'normal');
    doc.addFont('Pretendard-Medium.ttf', 'PretendardMedium', 'normal');
    doc.addFont('Pretendard-Regular.ttf', 'PretendardRegular', 'normal');
    doc.addFont('Pretendard-Light.ttf', 'PretendardLight', 'normal');
    doc.addFont('Pretendard-ExtraLight.ttf', 'PretendardExtraLight', 'normal');
    doc.addFont('Pretendard-Thin.ttf', 'PretendardThin', 'normal');

    // doc.internal.pageSize.setWidth(1200);
    doc.setDisplayMode('fullheight', 'continuous', 'UseOutlines');


    doc.setProperties({
      title, //  T('헬스당뇨트윈 플랫폼 보고서');
      subject: title,
      author: T(''),
      creator: T('wellsee.kt.co.kr'),
      keywords: T('당뇨'),
    });

    const start_time = Luxon.DateTime.fromFormat(period_start, 'yyyyMMdd').toFormat('yy.MM.dd');
    const end_time = Luxon.DateTime.fromFormat(period_end, 'yyyyMMdd').toFormat('yy.MM.dd');
    const data = {
      doctor_info,
      patient_no,
      period_start,
      period_end,
      start_time,
      end_time,
      patient_info: { ...patient_hash }[patient_no],

      glocose_data,
      diet_data,
      calories_data,
      steps_data,
      insulin_data,

      glocose_high_list,
      glocose_low_list,

      glocose_profile_chart,
      glocose_tir_chart,
      glocose_distrubution_bar_chart,
      glocose_distrubution_scatter_chart,
      glocose_pattern_weekday_chart,
      glocose_pattern_weekend_chart,
      diet_distrubution_doughnut_chart,
      diet_distrubution_scatter_chart,
      diet_meal_analysis_list,
      diet_food_analysis_list,
      diet_statistics_chart,
      diet_daily_statistics,
      step_analysis_day_chart,
      step_analysis_week_chart,
      step_statistics_chart,
      calory_analysis_day_chart,
      calory_analysis_week_chart,
      calory_statistics_chart,
      exercise_daily_statistics_list,
      insulin_distrubution_doughnut_chart,
      insulin_distrubution_scatter_chart,
      insulin_bolus_analysis_list,
      insulin_statistics_chart,
      insulin_pattern_time_list,
      insulin_statistics_day_chart,
      insulin_stat_list,
    };

    if (pdf_targets.B) {
      await drawPage1(doc, T('혈당'), '1/2', data, 'glocose_data');
      doc.addPage();
      await drawPage2(doc, T('혈당'), '2/2', data, 'glocose_data');
    }
    if (pdf_targets.D) {
      if (pdf_targets.B) doc.addPage();
      await drawPage3(doc, T('식사'), '1/2', data, 'diet_data');
      doc.addPage();
      await drawPage4(doc, T('식사'), '2/2', data, 'diet_data');
    }
    if (pdf_targets.E) {
      if (pdf_targets.B || pdf_targets.D) {
        doc.addPage();
      }

      await drawPage5(doc, T('활동(평균걸음수)'), '1/2', data, 'steps_data');
      doc.addPage();
      await drawPage6(doc, T('활동(평균걸음수)'), '2/2', data, 'steps_data');
      doc.addPage();
      await drawPage7(doc, T('활동(소모칼로리)'), '1/2', data, 'calories_data');
      doc.addPage();
      await drawPage8(doc, T('활동(소모칼로리)'), '2/2', data, 'calories_data');
    }
    if (pdf_targets.I) {
      if (pdf_targets.B || pdf_targets.D || pdf_targets.E) {
        doc.addPage();
      }
      await drawPage9(doc, T('인슐린'), '1/2', data, 'insulin_data');
      doc.addPage();
      await drawPage10(doc, T('인슐린'), '2/2', data, 'insulin_data');
    }

    doc.save(title + '.pdf');
    // const blob = doc.output('blob', { filename });
    // const url = URL.createObjectURL(blob);
    // window.open(url);
    // URL.revokeObjectURL(url);

    // PDF 생성 종료처리
    setTimeout(() => {
      Object.keys(data).forEach(key => {
        delete data[key];
      });
      reset();
      setDownloading(() => ({ uninitialized: true }));
      download_processing = false;
    }, 6000);
  }, [patient_no, diff_days, period_start, period_end, doctor_info, patient_hash, glocose_data, diet_data, calories_data, steps_data, insulin_data, glocose_high_list, glocose_low_list, glocose_profile_chart, glocose_tir_chart, glocose_distrubution_bar_chart, glocose_distrubution_scatter_chart, glocose_pattern_weekday_chart, glocose_pattern_weekend_chart, diet_distrubution_doughnut_chart, diet_distrubution_scatter_chart, diet_meal_analysis_list, diet_food_analysis_list, diet_statistics_chart, diet_daily_statistics, step_analysis_day_chart, step_analysis_week_chart, step_statistics_chart, calory_analysis_day_chart, calory_analysis_week_chart, calory_statistics_chart, exercise_daily_statistics_list, insulin_distrubution_doughnut_chart, insulin_distrubution_scatter_chart, insulin_bolus_analysis_list, insulin_statistics_chart, insulin_pattern_time_list, insulin_statistics_day_chart, insulin_stat_list, pdf_targets.B, pdf_targets.D, pdf_targets.E, pdf_targets.I, reset, setDownloading]);

  const [waiting, setWaiting] = React.useState(0);
  React.useEffect(() => {
    if (downloading.uninitialized) {
      setWaiting(() => 0);
    } else if (downloading.processing && disabled) {
      const timeoutId = setTimeout(() => setWaiting((w) => w + 1), 1000);
      return () => {
        clearTimeout(timeoutId);
      };
    } else if (!downloading.error && !disabled) {
      onDownload();
    }
  }, [disabled, downloading.error, downloading.processing, downloading.uninitialized, onDownload, waiting]);

  const onDownloadClick = React.useCallback(async () => {
    if (downloading.uninitialized) {
      reset();
      setDownloading(() => ({ processing: true }));
      setWaiting(() => 1);
    }
  }, [downloading.uninitialized, reset, setDownloading]);

  const onDropdownClick = React.useCallback(() => {
    open(floatingInfo);
  }, [open, floatingInfo]);

  React.useEffect(() => {
    update(floatingInfo);
  }, [floatingInfo, update]);

  const selector = React.useMemo(() => {
    return (
      <FocusableButton
        {...{ ref, onClick: onDropdownClick }}
      >
        <svg width={19} height={18} viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_2720_4072)">
            <path d="M5.9574 6.4425L9.3999 9.8775L12.8424 6.4425L13.8999 7.5L9.3999 12L4.8999 7.5L5.9574 6.4425Z" fill="#999999" />
          </g>
          <defs>
            <clipPath id="clip0_2720_4072">
              <rect width={18} height={18} fill="white" transform="translate(0.399902)" />
            </clipPath>
          </defs>
        </svg>
      </FocusableButton >
    );
  }, [onDropdownClick]);

  return (
    <HBox
      {...ex('DownloadButton', props)}
      {...{ ref: containerRef }}
    >
      <FocusableButton
        {...{
          onClick: onDownloadClick,
        }}
      >
        <svg width={19} height={18} viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_2720_4066)">
            <path d="M9.3999 12L5.6499 8.25L6.6999 7.1625L8.6499 9.1125V3H10.1499V9.1125L12.0999 7.1625L13.1499 8.25L9.3999 12ZM3.3999 15V11.25H4.8999V13.5H13.8999V11.25H15.3999V15H3.3999Z" fill="#999999" />
          </g>
          <defs>
            <clipPath id="clip0_2720_4066">
              <rect width={18} height={18} fill="white" transform="translate(0.399902)" />
            </clipPath>
          </defs>
        </svg>
        <div>{T('다운로드')}</div>
      </FocusableButton>
      {selector}
    </HBox >
  );
});
